/**
 * Close the software keyboard when the person turns from a field to the list
 * under it.
 *
 * The case this exists for is the search overlay: results update live under
 * the input as the person types, and on a phone the keyboard covers roughly
 * half of them. Dragging the list is the gesture that says "I am reading now,
 * not typing" — so that is what dismisses the keyboard.
 *
 * ── TOUCH ONLY, AND THAT IS THE WHOLE POINT ────────────────────────────────
 * It listens for `touchmove`, not `scroll`. A `scroll` event cannot tell a
 * finger from a mouse wheel from a PROGRAMMATIC scroll, and search scrolls
 * itself every time the ↑/↓ cursor moves a row into view (`reveal()`):
 * dismissing on `scroll` would blur the input on every arrow key and take
 * desktop's keyboard navigation down with it. A `touchmove` is a finger, by
 * specification — the browser's own `pointerType === "touch"` — and neither a
 * wheel nor `scrollTop = …` produces one.
 *
 * `ui/gesture` already reaches into `src/native` (`hold-to-record.tsx`,
 * `scrubber.tsx`); `hideKeyboard()` is the Capacitor call on the phone and a
 * `blur()` of the focused element on the web, which is what closes a mobile
 * browser's keyboard.
 */
import { type RefObject, useEffect } from "react";
import { hideKeyboard } from "../../native";

/**
 * The `<input type>`s that raise a software keyboard. Everything else — a
 * checkbox, a range, a file picker, a date wheel — either has no keyboard or
 * has a picker the person did not ask us to close.
 */
const TEXT_ENTRY_TYPES = new Set([
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

/** Is `element` something the phone raised a keyboard for? */
function isTextEntry(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  return (
    element instanceof HTMLInputElement && TEXT_ENTRY_TYPES.has(element.type)
  );
}

/**
 * For a gesture that began inside the scroller `ref` points at, the FIRST
 * `touchmove` closes the keyboard, if a text field holds focus.
 *
 * Once per gesture, not once per move: `touchmove` repeats at frame rate and
 * `hideKeyboard()` is an async bridge call, so a single drag would queue
 * dozens of them. `touchstart` arms the next one. A touch sequence keeps the
 * element it started on as its target, so "began inside" is what `contains`
 * answers on every move of the drag.
 *
 * ── WHY THE LISTENERS SIT ON THE DOCUMENT ──────────────────────────────────
 * They used to sit on `ref.current`, read once when this effect ran. In the
 * search overlay that element does not exist yet at that moment: Base UI's
 * `Dialog.Portal` renders `null` until its store says `mounted`, so the popup
 * subtree — the results scroller with it — arrives a commit LATER. The effect
 * therefore saw `null`, returned, and never ran again, because its only
 * dependency is the ref OBJECT, whose identity never changes. Nothing was
 * red: the hook was imported, called and attached to the right element in the
 * JSX, and its story mounted the scroller in the first commit and passed.
 * On the container, dragging the results left the keyboard up (T122,
 * 2026-09-21). Reading `ref.current` at EVENT time is what makes the late
 * mount irrelevant.
 */
export function useDismissKeyboardOnTouchScroll<T extends HTMLElement>(
  ref: RefObject<T | null>
): void {
  useEffect(() => {
    let handled = false;
    // Read inside the listener, never in a `useCallback`: the React Compiler
    // skips a component that reads `.current` from one (T-168).
    const began = (event: TouchEvent) => {
      const scroller = ref.current;
      return (
        scroller !== null &&
        event.target instanceof Node &&
        scroller.contains(event.target)
      );
    };
    const arm = (event: TouchEvent) => {
      if (began(event)) {
        handled = false;
      }
    };
    const dismiss = (event: TouchEvent) => {
      if (handled || !began(event)) {
        return;
      }
      handled = true;
      if (isTextEntry(document.activeElement)) {
        // Rejecting is not worth a broken scroll: the keyboard stays up.
        hideKeyboard().catch(() => undefined);
      }
    };

    // Passive: neither listener calls `preventDefault`, and saying so keeps
    // the browser from waiting on them before it scrolls.
    document.addEventListener("touchstart", arm, { passive: true });
    document.addEventListener("touchmove", dismiss, { passive: true });
    return () => {
      document.removeEventListener("touchstart", arm);
      document.removeEventListener("touchmove", dismiss);
    };
  }, [ref]);
}
